import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import moment from "moment";
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import api from "../../../components/services/api";

// คำนวณ amount / tax / total ของแต่ละแถวใหม่ (mirror ฝั่ง backend saleorderUpdateExtraExpenseInvoice)
const recompute = (row) => {
  const qty = +row.Quantity || 0;
  const price = +row.PricePerUnit || 0;
  const taxPct = +row.TaxPercent || 0;
  const whtPct = +row.WithholdingTaxPercent || 0;
  const ex = +row.ExchangeRate || 1;

  const amount = qty * price;
  const taxAmount = (amount * taxPct) / 100;
  const whtAmount = (amount * whtPct) / 100;
  const totalAmount = amount + taxAmount - whtAmount;

  return {
    ...row,
    Amount: amount,
    TaxAmount: taxAmount,
    WithholdingTaxAmount: whtAmount,
    TotalAmount: totalAmount,
    AmountTHB: amount * ex,
    TaxAmountTHB: taxAmount * ex,
    WithholdingTaxAmountTHB: whtAmount * ex,
    TotalAmountTHB: totalAmount * ex,
  };
};

const money = (n) => (+n || 0).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");

// Swal.fire ที่ดัน z-index ให้ลอยเหนือ MUI Dialog (default MUI z-index 1300 > swal 1060 => เด้งหลัง modal)
const fire = (opts) =>
  Swal.fire({
    ...opts,
    didOpen: (popup) => {
      const container = Swal.getContainer();
      if (container) container.style.zIndex = 20000;
      if (opts.didOpen) opts.didOpen(popup);
    },
  });

function CostLegSection({ legKey, leg, currency, saving, onHeaderChange, onRowChange, onAddRow, onRemoveRow, onSave }) {
  if (!leg) {
    return (
      <Box sx={{ marginTop: "10px" }}>
        <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#30318d" }}>
          {legKey === "Pickup" ? "Pick-up" : "Return"} Cost
        </Typography>
        <Typography sx={{ color: "#8E8C8C", marginTop: "6px" }}>
          ยังไม่มีรถ ({legKey === "Pickup" ? "Pick-up" : "Return"} Truck No.) จึงไม่สามารถแสดงค่าใช้จ่ายได้
        </Typography>
      </Box>
    );
  }

  const rows = leg.Detail || [];
  const sumTHB = rows.reduce((a, b) => a + (+b.AmountTHB || 0), 0);
  const sumTotalTHB = rows.reduce((a, b) => a + (+b.TotalAmountTHB || 0), 0);

  return (
    <Box sx={{ marginTop: "8px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#30318d" }}>
          {legKey === "Pickup" ? "Pick-up" : "Return"} Cost
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: leg.linked ? "#2e7d32" : "#ed6c02",
            border: "1px solid",
            borderRadius: "10px",
            padding: "1px 8px",
          }}
        >
          {leg.linked ? "Linked (Sale Order)" : "Preview (from vehicle)"}
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ marginTop: "2px" }}>
        <Grid item xs={4}>
          <TextField
            label="Invoice No."
            size="small"
            fullWidth
            value={leg.InvoiceNo || ""}
            onChange={(e) => onHeaderChange(legKey, "InvoiceNo", e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Bill To"
            size="small"
            fullWidth
            value={leg.BillTo || ""}
            onChange={(e) => onHeaderChange(legKey, "BillTo", e.target.value)}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Pay To"
            size="small"
            fullWidth
            value={leg.PayTo || ""}
            onChange={(e) => onHeaderChange(legKey, "PayTo", e.target.value)}
          />
        </Grid>
      </Grid>

      <Box sx={{ marginTop: "10px", overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 900 }}>
          <TableHead sx={{ backgroundColor: "#e7e7e7" }}>
            <TableRow>
              <TableCell sx={{ minWidth: 200 }}>Charge</TableCell>
              <TableCell sx={{ minWidth: 90 }}>Quantity</TableCell>
              <TableCell sx={{ minWidth: 110 }}>Price / Unit</TableCell>
              <TableCell sx={{ minWidth: 90 }}>Tax %</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Withholding %</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Currency</TableCell>
              <TableCell sx={{ minWidth: 110 }}>Exchange Rate</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Amount</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Total Amount</TableCell>
              <TableCell sx={{ minWidth: 80 }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={r.Charge || ""}
                    onChange={(e) => onRowChange(legKey, r.id, "Charge", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={r.Quantity}
                    onChange={(e) => onRowChange(legKey, r.id, "Quantity", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={r.PricePerUnit}
                    onChange={(e) => onRowChange(legKey, r.id, "PricePerUnit", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={r.TaxPercent}
                    onChange={(e) => onRowChange(legKey, r.id, "TaxPercent", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={r.WithholdingTaxPercent}
                    onChange={(e) => onRowChange(legKey, r.id, "WithholdingTaxPercent", e.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Currency</InputLabel>
                    <Select
                      label="Currency"
                      value={r.CurrencyId || ""}
                      onChange={(e) => onRowChange(legKey, r.id, "CurrencyId", e.target.value)}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {currency?.map((c) => (
                        <MenuItem key={c.CurrencyId} value={c.CurrencyId}>
                          {c.CurrencyCode}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    type="number"
                    value={r.ExchangeRate}
                    onChange={(e) => onRowChange(legKey, r.id, "ExchangeRate", e.target.value)}
                  />
                </TableCell>
                <TableCell align="right">{money(r.Amount)}</TableCell>
                <TableCell align="right">{money(r.TotalAmount)}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => onRemoveRow(legKey, r.id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ color: "#8E8C8C" }}>
                  ไม่มีรายการค่าใช้จ่าย (เพิ่มรายการได้)
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", marginTop: "10px", gap: "20px" }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => onAddRow(legKey)}
          sx={{ color: "#0d6efd", borderColor: "#0d6efd" }}
        >
          <AddIcon fontSize="small" /> Add Row
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Typography sx={{ fontSize: "14px" }}>
          Amount (THB): <b>{money(sumTHB)}</b>
        </Typography>
        <Typography sx={{ fontSize: "14px" }}>
          Total (THB): <b>{money(sumTotalTHB)}</b>
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={saving}
          onClick={() => onSave(legKey)}
          sx={{ background: "#80c340", width: 90 }}
        >
          {saving ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </Box>
    </Box>
  );
}

export default function CostDialog({ open, onClose, saleOrderId, container }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState("");
  const [currency, setCurrency] = useState([]);
  const [legs, setLegs] = useState({ Pickup: null, Return: null });
  const [errMsg, setErrMsg] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  const getCurrency = async () => {
    try {
      const res = await api.get(`api/master/currency`);
      setCurrency(res?.data || []);
    } catch (e) {
      console.log(e);
    }
  };

  const normalizeLeg = (leg) => {
    if (!leg) return null;
    return {
      ...leg,
      Detail: (leg.Detail || []).map((d, i) => recompute({ id: i + 1, ...d })),
    };
  };

  const fetchCost = async () => {
    if (!open || !container?.ContainerNo) return;
    setErrMsg("");
    // กัน request /cost/undefined กรณีหน้ายังไม่มี SaleOrderId (เช่น API ยังไม่ได้อัปเดต field นี้)
    if (!saleOrderId) {
      setLegs({ Pickup: null, Return: null });
      setErrMsg("ไม่พบ SaleOrderId ของงานนี้ — ลองรีโหลดหน้า (ถ้ายังไม่หาย แปลว่า API ยังไม่ได้อัปเดต ต้อง deploy ตัวใหม่)");
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`api/containermanagement/cost/${saleOrderId}`, {
        params: {
          voyIn: container.VoyIn || "",
          containerNo: container.ContainerNo || "",
          pickupTruckNo: container.PickupTruckNo || "",
          returnTruckNo: container.ReturnTruckNo || "",
        },
      });
      const d = res?.data || {};
      setLegs({ Pickup: normalizeLeg(d.Pickup), Return: normalizeLeg(d.Return) });
    } catch (e) {
      console.log(e);
      setLegs({ Pickup: null, Return: null });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      if (!currency.length) getCurrency();
      fetchCost();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, container?.ContainerNo, container?.PickupTruckNo, container?.ReturnTruckNo]);

  const onHeaderChange = (legKey, field, value) => {
    setLegs((prev) => ({ ...prev, [legKey]: { ...prev[legKey], [field]: value } }));
  };

  const onRowChange = (legKey, id, field, value) => {
    setLegs((prev) => {
      const leg = prev[legKey];
      if (!leg) return prev;
      const Detail = leg.Detail.map((r) => (r.id === id ? recompute({ ...r, [field]: value }) : r));
      return { ...prev, [legKey]: { ...leg, Detail } };
    });
  };

  const onAddRow = (legKey) => {
    setLegs((prev) => {
      const leg = prev[legKey];
      if (!leg) return prev;
      const nextId = (leg.Detail.slice(-1)[0]?.id || 0) + 1;
      const newRow = recompute({
        id: nextId,
        ExtraExpenseDetailId: null,
        Charge: "",
        Quantity: 1,
        PricePerUnit: 0,
        TaxPercent: 0,
        WithholdingTaxPercent: 0,
        CurrencyId: "",
        CurrencyCode: "",
        ExchangeRate: 1,
      });
      return { ...prev, [legKey]: { ...leg, Detail: [...leg.Detail, newRow] } };
    });
  };

  const onRemoveRow = (legKey, id) => {
    setLegs((prev) => {
      const leg = prev[legKey];
      if (!leg) return prev;
      return { ...prev, [legKey]: { ...leg, Detail: leg.Detail.filter((r) => r.id !== id) } };
    });
  };

  const onSave = async (legKey) => {
    const leg = legs[legKey];
    if (!leg) return;
    if (!leg.InvoiceNo) {
      fire({ title: "กรุณาระบุ Invoice No.", icon: "warning", confirmButtonColor: "#3085d6" });
      return;
    }

    setSaving(legKey);
    try {
      const rows = leg.Detail.map((r) => recompute(r));

      const body = {
        SaleOrderId: saleOrderId,
        VoyIn: container?.VoyIn || "",
        ContainerNo: container?.ContainerNo || "",
        Leg: legKey,
        ExtraExpenseId: leg.ExtraExpenseId || null,
        InvoiceNo: leg.InvoiceNo,
        IssueDate: leg.IssueDate ? moment(leg.IssueDate).format("yyyy-MM-DD") : moment().format("yyyy-MM-DD"),
        BillTo: leg.BillTo || "",
        PayTo: leg.PayTo || "",
        Detail: rows.map((r) => ({
          ExtraExpenseDetailId: r.ExtraExpenseDetailId || null,
          Charge: r.Charge || "",
          Quantity: +r.Quantity || 0,
          PricePerUnit: +r.PricePerUnit || 0,
          TaxPercent: +r.TaxPercent || 0,
          WithholdingTaxPercent: +r.WithholdingTaxPercent || 0,
          CurrencyId: r.CurrencyId || "",
          ExchangeRate: +r.ExchangeRate || 1,
        })),
      };

      const res = await api.post(`api/containermanagement/cost/save`, body);
      if (res?.meta?.status) {
        await fire({
          title: res.meta.message,
          icon: "success",
          confirmButtonColor: "#3085d6",
          confirmButtonText: "OK",
        });
        fetchCost();
      } else {
        fire({
          title: res?.meta?.message || "Save failed",
          icon: "error",
          confirmButtonColor: "#3085d6",
        });
      }
    } catch (e) {
      console.log(e);
      fire({ title: "Save failed", icon: "error", confirmButtonColor: "#3085d6" });
    } finally {
      setSaving("");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ color: "#30318d", fontWeight: 700 }}>
        Cost - Container {container?.ContainerNo || ""}
      </DialogTitle>
      <DialogContent dividers>
        {errMsg ? (
          <Typography sx={{ color: "#d32f2f", padding: "20px", textAlign: "center" }}>
            {errMsg}
          </Typography>
        ) : loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: "40px" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: "divider", marginBottom: "12px" }}
            >
              <Tab label="Pick-up Cost" />
              <Tab label="Return Cost" />
            </Tabs>
            <Box sx={{ display: activeTab === 0 ? "block" : "none" }}>
              <CostLegSection
                legKey="Pickup"
                leg={legs.Pickup}
                currency={currency}
                saving={saving === "Pickup"}
                onHeaderChange={onHeaderChange}
                onRowChange={onRowChange}
                onAddRow={onAddRow}
                onRemoveRow={onRemoveRow}
                onSave={onSave}
              />
            </Box>
            <Box sx={{ display: activeTab === 1 ? "block" : "none" }}>
              <CostLegSection
                legKey="Return"
                leg={legs.Return}
                currency={currency}
                saving={saving === "Return"}
                onHeaderChange={onHeaderChange}
                onRowChange={onRowChange}
                onAddRow={onAddRow}
                onRemoveRow={onRemoveRow}
                onSave={onSave}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ color: "#80c340", borderColor: "#80c340" }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
